import React, { FC } from "react"

import { Card, Stack, ThemeIcon, Text, Group, MantineColor, Skeleton } from "@mantine/core"
import { IconArrowDownRight, IconArrowUpRight, TablerIconsProps } from "@tabler/icons-react"

type StatCardProps = {
    label: string
    value: number
    icon: FC<TablerIconsProps>
    color?: MantineColor
    totalValue?: number
    prevValue?: number
    loading?: boolean
}

const StatCard: FC<StatCardProps> = ({ label, value, icon: Icon, color, totalValue, prevValue, loading }) => {
    const diff = prevValue ? (value - prevValue) / value : null

    return (
        <Card p="xs" withBorder>
            <Group align="start" position="apart" noWrap>
                <Stack spacing={0} p="md" pb="xs">
                    {loading ? (
                        <>
                            <Skeleton circle height="4rem" />
                            <Skeleton height="1.25rem" width="12rem" mt="xl" mb="xs" />
                            <Group spacing="xs" align="end">
                                <Skeleton height="2.25rem" width="6rem" />
                                <Skeleton height="1.25rem" width="4rem" />
                            </Group>
                        </>
                    ) : (
                        <>
                            <ThemeIcon variant="light" size="4rem" radius="xl" color={color}>
                                <Icon size="2.25rem" stroke={1.5} />
                            </ThemeIcon>
                            <Text
                                weight={500}
                                color="dimmed"
                                lh={1}
                                mt="xl"
                                sx={theme => ({ fontFamily: theme.headings.fontFamily })}
                            >
                                {label}
                            </Text>
                            <Group align="baseline" noWrap>
                                <Text sx={theme => ({ fontFamily: theme.headings.fontFamily })}>
                                    <Text size="2rem" weight="bold" span>
                                        {value}
                                    </Text>
                                    {typeof totalValue === "number" && (
                                        <Text size="1.5rem" weight={200} span>
                                            /{totalValue}
                                        </Text>
                                    )}
                                </Text>
                                {diff && (
                                    <Text color={diff >= 0 ? "green" : "red"} size="sm" span>
                                        <Group spacing={0} noWrap>
                                            {Number(diff * 100).toFixed(0)}%{" "}
                                            {diff > 0 ? (
                                                <IconArrowUpRight size="1.125rem" stroke={1.5} />
                                            ) : (
                                                <IconArrowDownRight size="1.125rem" stroke={1.5} />
                                            )}
                                        </Group>
                                    </Text>
                                )}
                            </Group>
                        </>
                    )}
                </Stack>
            </Group>
        </Card>
    )
}

export default StatCard
